<template>
  <div class="container-fluid border-none py-5">
    <!-- Show tabs -->
    <ul class="nav nav-tabs d-flex justify-content-start w-auto">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'sfpinfo' }" @click="switchTab('sfpinfo')">SFP Info</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'intfstate' }" @click="switchTab('intfstate')">Interface State</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'portupdate' }" @click="switchTab('portupdate')">Port Shutdown</a>
      </li>
    </ul>
    <div class="row p-0 m-0">
      <!-- Left Side: Basic Info Box -->
      <div
        class="col-md-3 d-flex flex-column p-0 m-0"
        style="padding-right: 30px !important; row-gap: 30px !important"
      >
        <!-- Switch Info Card -->
        <div class="_card">
          <template v-if="loadingBasicInfo">
            <Skalaton />
          </template>
          <template v-else>
            <div class="tabs-container">
              <div class="tab-content">
                <h5 class="switch-info-header">
                  <span>Switch Info</span>
                </h5>
                <p class="text-sm m-0 font-weight-bold">Switch ID : {{ switchData?.oltid || switchData?.id || 'N/A' }}</p>
                <p class="text-sm m-0 font-weight-bold">Switch Name : {{ switchData?.sw_name || 'N/A' }}</p>
                <p class="text-sm m-0 font-weight-bold">IP : {{ switchData?.sw_ip || 'N/A' }}</p>
                <p class="text-sm m-0 font-weight-bold">Port : {{ switchData?.sw_port || 'N/A' }}</p>
                <p class="text-sm m-0 font-weight-bold">Description : {{ switchData?.description || 'N/A' }}</p>
              </div>
            </div>
          </template>
        </div>

        <!-- Basic Information Card -->
        <div class="_card" v-if="isBdcomSwitch">
          <template v-if="loadingBasicInfo">
            <Skalaton />
          </template>
          <template v-else-if="basicInfo">
            <div class="tabs-container">
              <div class="tab-content">
                <h5 class="switch-info-header">
                  <span>Basic Information</span>
                </h5>
                <SwitchBasicInfo :basicInfo="basicInfo" />
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Right Side: Large box -->
      <div class="col-md-9 _card p-0 d-flex flex-row">
        <div class="w-100">
          <!-- Controls Row -->
          <div class="d-flex justify-content-between align-items-center mx-3 py-2 flex-wrap switch-controls-row">
            <!-- Left controls: Title -->
            <div class="d-flex align-items-center flex-nowrap g-3">
              <h5 class="mb-0 d-flex" style="padding-left: 0;">
                {{ getTableTitle() }}
              </h5>
            </div>

            <!-- Right controls: Refresh -->
            <div class="d-flex align-items-center flex-nowrap">
              <span class="d-flex badge bg-info text-white cursor-pointer" style="color: rgb(242 242 242) !important;" v-if="refreshing" @click="refreshCurrentTab"
                :disabled="refreshing">
                <i class="fas fa-spinner fa-spin mx-1"></i> Refreshing...
              </span>
              <span class="d-flex badge bg-info text-white cursor-pointer" style="color: rgb(242 242 242) !important;" v-else @click="refreshCurrentTab"
                :disabled="refreshing">
                <i class="fas fa-sync-alt mx-1"></i> Refresh
              </span>
            </div>
          </div>

          <div class="table-responsive p-0">
            <template v-if="loadingBasicInfo">
              <Skalaton />
            </template>
            <template v-else>
              <!-- Loading states for each tab -->
              <div v-if="activeTab === 'sfpinfo' && loadingSfpInfo" class="text-center p-4">
                <div class="spinner-border spinner-border-sm" role="status"></div>
                <span class="ms-2">Loading SFP Information...</span>
              </div>

              <div v-if="activeTab === 'intfstate' && loadingIntfState" class="text-center p-4">
                <div class="spinner-border spinner-border-sm" role="status"></div>
                <span class="ms-2">Loading Interface State Information...</span>
              </div>

              <div v-if="activeTab === 'portupdate' && loadingPortUpdate" class="text-center p-4">
                <div class="spinner-border spinner-border-sm" role="status"></div>
                <span class="ms-2">Loading Port Shutdown Information...</span>
              </div>

              <!-- Show message if not BDCOM switch -->
              <div v-if="!isBdcomSwitch && !loadingBasicInfo" class="text-center p-4">
                <Skalaton />
              </div>

              <!-- SFP Info Table -->
              <SFPInfoTable 
                v-if="isBdcomSwitch && activeTab === 'sfpinfo' && !loadingSfpInfo && basicInfo"
                :sfpInfo="sfpInfo"
                :basicInfo="basicInfo"
                :loading="loadingSfpInfo"
                :portDescriptions="portDescriptions"
              />

              <!-- Interface State Table -->
              <SintfStateTable 
                v-if="isBdcomSwitch && activeTab === 'intfstate' && !loadingIntfState && basicInfo"
                :intfState="intfState"
                :basicInfo="basicInfo"
                :loading="loadingIntfState"
                :portDescriptions="portDescriptions"
              />

              <!-- Port Shutdown Table -->
              <PortUpdateVlaueTable 
                v-if="isBdcomSwitch && activeTab === 'portupdate' && !loadingPortUpdate && basicInfo"
                :portUpdateValue="portUpdateValue"
                :basicInfo="basicInfo"
                :loading="loadingPortUpdate"
                :switchId="$route.params.id"
                :portDescriptions="portDescriptions"
              />
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast message -->
    <div v-if="showToast" class="custom-toast" :class="toastType">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import { useSwitchDetailsBdcomSwitchApi } from '../services/switch-details-bdcom-swicth.service';
import showSwal from '../mixins/showSwal';
import Skalaton from '@/components/Skalaton.vue';
import SFPInfoTable from './components/SwitchDetails/SFPInfoTable.vue';
import SintfStateTable from './components/SwitchDetails/SintfStateTable.vue';
import PortUpdateVlaueTable from './components/SwitchDetails/PortUpdateVlaueTable.vue';
import SwitchBasicInfo from './components/SwitchDetails/SwitchBasicInfo.vue';
import dataList from '../services/get-sw.service';
import getBrands from '../services/get-brands.service';

export default {
  name: "SwitchDetails",
  components: {
    Skalaton,
    SFPInfoTable,
    SintfStateTable,
    PortUpdateVlaueTable,
    SwitchBasicInfo,
  },
  data() {
    return {
      switchData: null,
      basicInfo: null,
      sfpInfo: null,
      intfState: null,
      portUpdateValue: null,
      portDescriptions: null,
      loadingBasicInfo: false,
      loadingSfpInfo: false,
      loadingIntfState: false,
      loadingPortUpdate: false,
      refreshing: false,
      activeTab: 'sfpinfo',
      showToast: false,
      toastMessage: '',
      toastType: '',
      brands: [],
      isBdcomSwitch: false,
    };
  },
  computed: {
    canLoadData() {
      return this.isBdcomSwitch;
    }
  },
  methods: {
    async switchTab(tab) {
      this.activeTab = tab;
      
      // Only fetch data if it's a BDCOM switch
      if (!this.isBdcomSwitch) return;
      
      // Fetch data for the active tab if not already loaded
      if (this.basicInfo) {
        if (tab === 'sfpinfo' && !this.sfpInfo) {
          await this.getSfpInfo();
        } else if (tab === 'intfstate' && !this.intfState) {
          await this.getIntfState();
        } else if (tab === 'portupdate' && !this.portUpdateValue) {
          await this.getPortUpdateValue();
        }
      }
    },

    async refreshCurrentTab() {
      if (this.refreshing) return;
      if (!this.isBdcomSwitch) return;
      
      this.refreshing = true;
      
      try {
        switch (this.activeTab) {
          case 'sfpinfo':
            await this.getSfpInfo(true);
            break;
            
          case 'intfstate':
            await this.getIntfState(true);
            break;
            
          case 'portupdate':
            await this.getPortUpdateValue(true);
            break;
        }
        
        // Refresh port descriptions as well
        await this.getPortDescriptions(true);
        
        // Show success feedback
        this.showToastMessage(`${this.getTableTitle()} data refreshed successfully`, 'success');
      } catch (error) {
        console.error('Error refreshing data:', error);
        this.showToastMessage('Failed to refresh data', 'error');
      } finally {
        this.refreshing = false;
      }
    },

    getTableTitle() {
      if (this.activeTab === 'sfpinfo') return 'SFP Info';
      if (this.activeTab === 'intfstate') return 'Interface State';
      if (this.activeTab === 'portupdate') return 'Port Shutdown';
      return '';
    },

    async getSwitchData() {
      try {
        const response = await dataList.getDataList();
        const switchId = this.$route.params.id;
        this.switchData = response.find(item => item.id == switchId || item.oltid == switchId);
        
        // Check if switch brand is BDCOM
        if (this.switchData && this.switchData.sw_brands) {
          await this.checkBrand();
        }
      } catch (error) {
        console.error("Error fetching switch data:", error);
      }
    },

    async checkBrand() {
      try {
        // Check if sw_brands is directly "BDCOM" string
        if (this.switchData.sw_brands === 'BDCOM') {
          this.isBdcomSwitch = true;
          return;
        }
        
        // If sw_brands is an ID, look it up in brands list
        if (typeof this.switchData.sw_brands === 'number' || (typeof this.switchData.sw_brands === 'string' && !isNaN(this.switchData.sw_brands))) {
          // Get brands list if not already loaded
          if (!this.brands || this.brands.length === 0) {
            this.brands = await getBrands.getBrands();
          }
          
          // Find the brand by ID
          const brand = this.brands.find(b => b.id == this.switchData.sw_brands);
          
          // Check if brand name is BDCOM
          if (brand && brand.sw_brand_name === 'BDCOM') {
            this.isBdcomSwitch = true;
            return;
          }
        }
        
        // If not BDCOM, set flag to false
        this.isBdcomSwitch = false;
        showSwal.methods.showSwal({
          type: 'warning',
          message: 'Switch details are only available for BDCOM switches.',
        });
      } catch (error) {
        console.error("Error checking brand:", error);
        this.isBdcomSwitch = false;
      }
    },

    async getBasicInfo() {
      if (!this.canLoadData) return;
      
      this.loadingBasicInfo = true;
      try {
        const swid = this.$route.params.id;
        const response = await useSwitchDetailsBdcomSwitchApi().getBasicInfo(swid);
        this.basicInfo = response;
      } catch (error) {
        console.error("Error fetching basic info:", error);
        showSwal.methods.showSwal({
          type: 'error',
          message: 'Failed to load basic information',
        });
      } finally {
        this.loadingBasicInfo = false;
      }
    },

    async getSfpInfo(forceRefresh = false) {
      if (!this.canLoadData) return;
      
      if (forceRefresh) {
        this.sfpInfo = null;
      }
      
      if (this.sfpInfo && !forceRefresh) return;
      
      this.loadingSfpInfo = true;
      try {
        const swid = this.$route.params.id;
        const response = await useSwitchDetailsBdcomSwitchApi().getSfpInfoData(swid);
        this.sfpInfo = response;
      } catch (error) {
        console.error("Error fetching SFP info:", error);
        showSwal.methods.showSwal({
          type: 'error',
          message: 'Failed to load SFP information',
        });
      } finally {
        this.loadingSfpInfo = false;
      }
    },

    async getIntfState(forceRefresh = false) {
      if (!this.canLoadData) return;
      
      if (forceRefresh) {
        this.intfState = null;
      }
      
      if (this.intfState && !forceRefresh) return;
      
      this.loadingIntfState = true;
      try {
        const swid = this.$route.params.id;
        const response = await useSwitchDetailsBdcomSwitchApi().getSintfStateData(swid);
        this.intfState = response;
      } catch (error) {
        console.error("Error fetching interface state:", error);
        showSwal.methods.showSwal({
          type: 'error',
          message: 'Failed to load interface state information',
        });
      } finally {
        this.loadingIntfState = false;
      }
    },

    async getPortUpdateValue(forceRefresh = false) {
      if (!this.canLoadData) return;
      
      if (forceRefresh) {
        this.portUpdateValue = null;
      }
      
      if (this.portUpdateValue && !forceRefresh) return;
      
      this.loadingPortUpdate = true;
      try {
        const swid = this.$route.params.id;
        const response = await useSwitchDetailsBdcomSwitchApi().getPortUpdateVlaue(swid);
        this.portUpdateValue = response;
      } catch (error) {
        console.error("Error fetching Port Shutdown:", error);
        showSwal.methods.showSwal({
          type: 'error',
          message: 'Failed to load Port Shutdown information',
        });
      } finally {
        this.loadingPortUpdate = false;
      }
    },

    async getPortDescriptions(forceRefresh = false) {
      if (!this.canLoadData) return;
      
      if (forceRefresh) {
        this.portDescriptions = null;
      }
      
      if (this.portDescriptions && !forceRefresh) return;
      
      try {
        const swid = this.$route.params.id;
        const response = await useSwitchDetailsBdcomSwitchApi().getPortDescription(swid);
        this.portDescriptions = response;
      } catch (error) {
        console.error("Error fetching port descriptions:", error);
        // Don't show error to user as this is supplementary data
        this.portDescriptions = null;
      }
    },

    showToastMessage(msg, type) {
      this.toastMessage = msg;
      this.toastType = type;
      this.showToast = true;
      setTimeout(() => { this.showToast = false; }, 2000);
    },

    getBrandName() {
      if (!this.switchData || !this.switchData.sw_brands) return 'Unknown';
      
      // If sw_brands is already a string (brand name), return it directly
      if (typeof this.switchData.sw_brands === 'string' && this.switchData.sw_brands !== '' && isNaN(this.switchData.sw_brands)) {
        return this.switchData.sw_brands;
      }
      
      // If sw_brands is an ID, look it up in brands list
      if (this.brands && this.brands.length > 0) {
        const brand = this.brands.find(b => b.id == this.switchData.sw_brands);
        return brand ? brand.sw_brand_name : 'Unknown';
      }
      
      return 'Unknown';
    },
  },
  async mounted() {
    await this.getSwitchData();
    // Only load data if it's a BDCOM switch
    if (this.isBdcomSwitch) {
      await this.getBasicInfo();
      // Load port descriptions
      await this.getPortDescriptions();
      // Load initial tab data
      await this.getSfpInfo();
    }
  },
};
</script>

<style scoped>
/* Clean tab styles - no borders */
.nav-tabs {
  border-bottom: none;
  margin-bottom: 15px;
}

.nav-tabs .nav-item {
  margin-bottom: 0;
}

.nav-tabs .nav-link {
  border: none;
  border-radius: 0;
  padding: 0.5rem 1rem;
  color: #495057;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  position: relative;
}

.nav-tabs .nav-link:hover {
  color: #0056b3;
  background-color: transparent;
}

.nav-tabs .nav-link.active {
  color: #0056b3;
  background-color: transparent;
  border: none;
  font-weight: 600;
}

/* Underline effect for active tab only */
.nav-tabs .nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #0056b3;
}

.tab-content {
  padding-top: 10px;
}

/* Dark mode support */
.dark-mode .nav-tabs .nav-link {
  color: #ccc;
}

.dark-mode .nav-tabs .nav-link:hover {
  color: #82D616;
}

.dark-mode .nav-tabs .nav-link.active {
  color: #82D616;
}

.dark-mode .nav-tabs .nav-link.active::after {
  background-color: #82D616;
}

._card {
  border-radius: 10px;
  padding: 0px 15px;  
  padding-bottom: 10px;
  color: rgba(0, 0, 0, 0.589);
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: visible;
}

.dark-mode ._card {
  background-color: #061f3e;
}

.switch-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 0;
  margin-bottom: 0;
}

.switch-controls-row {
  gap: 12px;
}

@media (max-width: 900px) {
  .switch-controls-row {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px;
  }
  .switch-controls-row > * {
    width: 100% !important;
    min-width: 0 !important;
    margin-bottom: 6px;
  }
}

.custom-toast {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 2000;
  background: #222;
  color: #fff;
  padding: 14px 28px;
  border-radius: 8px;
  font-size: 1.1rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  opacity: 0.95;
  transition: all 0.3s;
}

.custom-toast.success {
  background: #82D616;
  color: #181f2a;
}

.custom-toast.error {
  background: #e74c3c;
  color: #fff;
}

/* Spinner styles */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.text-center {
  text-align: center;
}
</style>

